import { BASE_URL, Image_BASE_URL } from "@/api/apiUrl";
import { useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, Search, UsersRound, X } from "lucide-react";
import { formatDate } from "@/lib/utils";
import AboutCardSkeleton from "../home/component/AboutCardSkeleton";
import slugify from "slugify";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

interface PastSeasonsResponse {
  success: boolean;
  message: string;
  data: Season[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNextPage: boolean;
    itemsPerPage: number;
  };
}
interface Season {
  id: number;
  title: string;
  season: number;
  tagLine: string;
  heroImage: string;
  cardImage: string;
  eventDate: string;
  registrationEndDate: string;
  eventSize: string;
  eventDetails: string;
  status: string;
  eventId: number;
}

const PastSeasons = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { ref, inView } = useInView();
  const [loaded, setLoaded] = useState(false);
  const skeletonCount = 6;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const {
    data,
    isSuccess,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ["pastSeasons", debouncedSearch],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.get<PastSeasonsResponse>(
        `${BASE_URL}/season/past-seasons`,
        {
          params: {
            search: debouncedSearch,
            page: pageParam,
            limit: 10,
          },
        }
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.pagination?.hasNextPage) {
        return undefined;
      }
      return lastPage.pagination.currentPage + 1;
    },
    initialPageParam: 1,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  if (status === "error") {
    return <div className="mt-36">Error: {error.message}</div>;
  }
  const slugifyTitle = (title: string) => {
    return slugify(title, { lower: true, strict: true });
  };
  const clearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
  };

  if (isSuccess && data.pages[0].data.length === 0) {
    return null;
  }
  return (
    <section
      id="get-started"
      className="mx-2 mb-56 lg:mt-0 max-w-6xl md:mx-auto"
    >
      <h1 className="text-4xl text-gray-700 font-bold text-center">
        Past Events
      </h1>
      <div className="w-16 h-1 bg-[#009FA6] mt-4 mb-12 rounded-full mx-auto" />
      <div className="mb-8 relative">
        <Input
          type="text"
          placeholder="Search past events..."
          value={search}
          onChange={handleSearch}
          className="w-full pl-10 pr-10 bg-gray-50"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        {search && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 transform -translate-y-1/2"
            onClick={clearSearch}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {status === "pending" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {Array.from({ length: skeletonCount }, (_, index) => (
            <AboutCardSkeleton key={index} index={index} />
          ))}
        </div>
      ) : (
        <>
          <div className="w-full mx-auto grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data?.pages.map((page) =>
              page.data.map((season: Season) => (
                <motion.div
                  key={season.id}
                  whileHover={{
                    scale: 1.02,
                    transition: { duration: 0.3 },
                  }}
                  className="h-full px-3 md:px-0"
                >
                  <Link
                    to={`/event/${slugifyTitle(
                      season.title + "-" + season.id
                    )}`}
                    className="block h-full"
                  >
                    <div className="bg-white relative rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col z-10">
                      <div className="flex-1 h-full">
                        {!loaded && (
                          <div className="w-full aspect-[calc(4*3+1)/8] absolute inset-0 bg-gray-200 animate-pulse" />
                        )}
                        <img
                          src={`${Image_BASE_URL}/images/event/${season.cardImage}`}
                          alt={season.title}
                          className={`w-full h-full aspect-[calc(4*3+1)/8] object-fill object-center transition-opacity duration-300 ${
                            loaded ? "opacity-100" : "opacity-0"
                          }`}
                          loading="lazy"
                          onLoad={() => setLoaded(true)}
                        />
                        <div className="absolute top-4 right-4 bg-gray-700 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          Season {season.season}
                        </div>
                      </div>

                      <div className="w-full h-full bg-gray-700 text-white p-6">
                        <div className="w-full">
                          <h2 className="text-white font-bold mb-2 line-clamp-2">
                            {season.title.length > 30
                              ? `${season.title.slice(0, 35)}...`
                              : season.title}
                          </h2>
                          <p className="text-gray-300 text-sm italic mb-4 line-clamp-1 mt-1 font-light">
                            {season.tagLine}
                          </p>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-5 h-5" />
                            <span>
                              {formatDate(new Date(season.eventDate))}
                            </span>
                          </div>

                          <div className="flex items-center space-x-2">
                            <UsersRound className="w-5 h-5 text-white" />
                            <span>{season.eventSize} Participants</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))
            )}
          </div>

          <div ref={ref} className="mt-6 text-center">
            {isFetchingNextPage && (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#009FA6]" />
                <span className="text-gray-600">Loading more...</span>
              </div>
            )}
            {!hasNextPage && data?.pages?.[0]?.data?.length > 0 && (
              <p className="text-gray-500 mt-20">No more events to load</p>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default PastSeasons;

